import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { StandardEvents } from '@shopify/events';

/**
 * SSC info rows under the buy buttons (blocks/ssc-shipping-note.liquid).
 * - Rows with details are buttons that open the block's one dialog at their section
 *   (`data-section`): the dialog scrolls to it and focuses its heading. Escape, the close button
 *   and a click on the backdrop close it (DialogComponent, which also locks page scroll); focus
 *   returns to the row that opened it.
 * - Membership points row: `[data-ssc-points]` follows the selected variant (product select
 *   event): round(price in whole currency units x `data-points-per`).
 *
 * @typedef {{ dialog?: HTMLDialogElement }} Refs
 * @extends {DialogComponent}
 */
class SscInfoRows extends DialogComponent {
  /** The dialog only renders when a row has details. */
  requiredRefs = [];

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {HTMLElement | null} */
  #opener = null;

  /** @type {string} */
  #section = '';

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener(DialogOpenEvent.eventName, this.#handleOpen, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#handleClose, { signal });

    if (this.dataset.pointsPer) {
      this.closest('.shopify-section, dialog')?.addEventListener(StandardEvents.productSelect, this.handleProductSelect, {
        signal,
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = undefined;
    this.#opener = null;
  }

  /**
   * Opens the dialog at the clicked row's section.
   * @param {MouseEvent} event
   */
  openSection(event) {
    const row = event.target;
    if (!(row instanceof HTMLElement) || !this.refs.dialog) return;

    this.#opener = row;
    this.#section = row.dataset.section ?? '';
    this.showDialog();
  }

  #handleOpen = () => {
    const { dialog } = this.refs;
    if (!dialog) return;

    const section = this.#section
      ? dialog.querySelector(`.ssc-info-rows__section[data-section="${CSS.escape(this.#section)}"]`)
      : null;
    const heading = section?.querySelector('.ssc-info-rows__section-title');
    const body = dialog.querySelector('.ssc-info-rows__dialog-body');

    if (body instanceof HTMLElement) {
      body.scrollTop = section instanceof HTMLElement ? section.offsetTop - body.offsetTop : 0;
    }
    if (heading instanceof HTMLElement) heading.focus({ preventScroll: true });
  };

  #handleClose = () => {
    const opener = this.#opener;
    this.#opener = null;
    if (opener?.isConnected) opener.focus({ preventScroll: true });
  };

  /**
   * Updates the points for the newly selected variant.
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail || !this.isConnected) return;

        const { newProduct, productId, resource } = detail;
        if (newProduct) {
          this.dataset.productId = String(newProduct.id);
        } else if (productId && String(productId) !== this.dataset.productId) {
          return;
        }

        const price = Number(resource?.price);
        if (!Number.isFinite(price)) return;

        const points = String(Math.round((price / 100) * (Number(this.dataset.pointsPer) || 0)));
        for (const element of this.querySelectorAll('[data-ssc-points]')) element.textContent = points;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[ssc-info-rows] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('ssc-info-rows')) customElements.define('ssc-info-rows', SscInfoRows);
